import type { CoreMessage as CoreMessageV4 } from '@internal/ai-sdk-v4';
import type { ModelMessage, ToolResultPart } from '@internal/ai-sdk-v5';

import type { IMastraLogger } from '../../../logger';
import type { MastraDBMessage } from '../state/types';
import { getResponseProviderItemId } from './response-item-metadata';
import type { ResponseItemIdProvider } from './response-item-metadata';

/**
 * Tool result with input field (Anthropic requirement)
 */
export type ToolResultWithInput = ToolResultPart & {
  input: Record<string, unknown>;
};

// ============================================================================
// Gemini Compatibility
// ============================================================================

/**
 * Ensures message array is compatible with Gemini API requirements.
 *
 * Gemini API requires:
 * 1. The first non-system message must be from the user role
 * 2. Cannot have only system messages - at least one user/assistant is required
 *
 * @param messages - Array of model messages to validate and fix
 * @param logger - Optional logger for warnings
 * @returns Modified messages array that satisfies Gemini requirements
 *
 * @see https://github.com/mastra-ai/mastra/issues/7287 - Tool call ordering
 * @see https://github.com/mastra-ai/mastra/issues/8053 - Single turn validation
 * @see https://github.com/mastra-ai/mastra/issues/13045 - Empty thread support
 */
export function ensureGeminiCompatibleMessages<T extends ModelMessage | CoreMessageV4>(
  messages: T[],
  logger?: IMastraLogger,
): T[] {
  const result = [...messages];

  // Ensure first non-system message is user
  const firstNonSystemIndex = result.findIndex(m => m.role !== 'system');

  if (firstNonSystemIndex === -1) {
    // Only system messages or empty — warn and pass through unchanged.
    // Providers that support system-only prompts (Anthropic, OpenAI) will work natively.
    // Providers that don't (Gemini) will return their own error.
    if (result.length > 0) {
      logger?.warn(
        'No user or assistant messages in the request. Some providers (e.g. Gemini) require at least one user message to generate a response.',
      );
    }
  } else if (result[firstNonSystemIndex]?.role === 'assistant') {
    // First non-system is assistant, insert user message before it
    result.splice(firstNonSystemIndex, 0, {
      role: 'user',
      content: '.',
    } as T);
  }

  return result;
}

// ============================================================================
// Anthropic Compatibility
// ============================================================================

/**
 * Ensures model messages are compatible with Anthropic API requirements.
 *
 * Anthropic API requires tool-result parts to include an 'input' field
 * that matches the original tool call arguments.
 *
 * @param messages - Array of model messages to transform
 * @param dbMessages - MastraDB messages to look up tool call args from
 * @returns Messages with tool-result parts enriched with input field
 *
 * @see https://github.com/mastra-ai/mastra/issues/11376 - Anthropic models fail with empty object tool input
 */
export function ensureAnthropicCompatibleMessages(
  messages: ModelMessage[],
  dbMessages: MastraDBMessage[],
): ModelMessage[] {
  return messages.map(msg => enrichToolResultsWithInput(msg, dbMessages));
}

/**
 * Removes orphan tool_use / tool_result blocks. Anthropic requires every tool_result
 * to be in the message immediately after its matching tool_use, and every tool_use
 * to have a matching tool_result in the next message. Recall windows can slice
 * through a parallel tool-call group and leave behind half a pair.
 */
export function sanitizeOrphanedToolPairs(messages: ModelMessage[]): ModelMessage[] {
  const filteredContents = messages.map(m => (Array.isArray(m.content) ? [...m.content] : null));

  for (let i = 0; i < messages.length; i++) {
    const current = messages[i]!;

    if (current.role === 'assistant' && Array.isArray(current.content)) {
      const useIds = new Set<string>();
      const inlineResultIds = new Set<string>();
      for (const part of current.content) {
        if (part.type === 'tool-call') useIds.add(part.toolCallId);
        else if (part.type === 'tool-result') inlineResultIds.add(part.toolCallId);
      }

      const next = messages[i + 1];
      const nextResultIds = new Set<string>();
      if (next && next.role === 'tool' && Array.isArray(next.content)) {
        for (const part of next.content) {
          if (part.type === 'tool-result') nextResultIds.add(part.toolCallId);
        }
      }

      const validPairs = new Set([...useIds].filter(id => inlineResultIds.has(id) || nextResultIds.has(id)));

      filteredContents[i] = filteredContents[i]!.filter(p => {
        if (p.type !== 'tool-call') return true;
        const tc = p as { toolCallId: string; providerExecuted?: boolean };
        // Provider-executed tools may be deferred (e.g. Anthropic web_search): the tool_use
        // can appear without a matching tool_result until the provider resumes on the next call.
        return tc.providerExecuted === true || validPairs.has(tc.toolCallId);
      });

      if (next && next.role === 'tool' && Array.isArray(next.content)) {
        filteredContents[i + 1] = filteredContents[i + 1]!.filter(
          p => p.type !== 'tool-result' || validPairs.has((p as { toolCallId: string }).toolCallId),
        );
      }
    } else if (current.role === 'tool' && Array.isArray(current.content)) {
      const prev = messages[i - 1];
      if (!prev || prev.role !== 'assistant' || !Array.isArray(prev.content)) {
        filteredContents[i] = filteredContents[i]!.filter(p => p.type !== 'tool-result');
      }
    }
  }

  const result: ModelMessage[] = [];
  for (let i = 0; i < messages.length; i++) {
    const original = messages[i]!;
    const filtered = filteredContents[i];
    if (filtered == null) {
      result.push(original);
      continue;
    }
    if (filtered.length === 0) continue;
    if (Array.isArray(original.content) && filtered.length === original.content.length) {
      result.push(original);
      continue;
    }
    result.push({ ...original, content: filtered } as ModelMessage);
  }

  return result;
}

/**
 * Enriches a single message's tool-result parts with input field
 */
function enrichToolResultsWithInput(message: ModelMessage, dbMessages: MastraDBMessage[]): ModelMessage {
  if (message.role !== 'tool' || !Array.isArray(message.content)) {
    return message;
  }

  return {
    ...message,
    content: message.content.map(part => {
      if (part.type === 'tool-result') {
        return {
          ...part,
          input: findToolCallArgs(dbMessages, part.toolCallId),
        } as ToolResultWithInput;
      }
      return part;
    }),
  } as ModelMessage;
}

// ============================================================================
// OpenAI-compatible Responses Compatibility
// ============================================================================

/**
 * Checks if a message part has an OpenAI reasoning itemId.
 *
 * OpenAI Responses reasoning items are tracked via `providerMetadata.openai.itemId`.
 * Each reasoning item has a unique itemId that must be preserved for proper deduplication.
 *
 * @param part - A message part to check
 * @returns true if the part has an OpenAI itemId
 *
 * @see https://github.com/mastra-ai/mastra/issues/9005 - OpenAI reasoning items filtering
 */
export function hasOpenAIReasoningItemId(part: unknown): boolean {
  return Boolean(getOpenAIReasoningItemId(part));
}

/**
 * Checks if a message part has an OpenAI-compatible Responses itemId.
 *
 * Provider-neutral Responses item IDs are tracked via provider metadata or
 * provider options fields such as `openai.itemId` or `azure.itemId`.
 */
export function hasResponseProviderItemId(part: unknown): boolean {
  return Boolean(getResponseProviderItemIdFromPart(part));
}

/**
 * Extracts an OpenAI itemId from a message part if present.
 *
 * This only inspects `providerMetadata.openai.itemId`; use
 * `getResponseProviderItemIdFromPart` for provider-aware Azure/OpenAI lookups.
 *
 * @param part - A message part to extract from
 * @returns The itemId string or undefined if not present
 */
export function getOpenAIReasoningItemId(part: unknown): string | undefined {
  if (!part || typeof part !== 'object') return undefined;
  const partAny = part as Record<string, unknown>;
  const providerMetadata = partAny.providerMetadata as Record<string, unknown> | undefined;
  const openaiMetadata = providerMetadata?.openai as Record<string, unknown> | undefined;
  return typeof openaiMetadata?.itemId === 'string' ? openaiMetadata.itemId : undefined;
}

export function getResponseProviderItemIdFromPart(
  part: unknown,
): { provider: ResponseItemIdProvider; itemId: string } | undefined {
  if (!part || typeof part !== 'object') return undefined;
  const partAny = part as Record<string, unknown>;

  return (
    getResponseProviderItemId(partAny.providerMetadata as Record<string, unknown> | undefined) ||
    getResponseProviderItemId(partAny.providerOptions as Record<string, unknown> | undefined)
  );
}

// ============================================================================
// Tool Call Args Lookup
// ============================================================================

/**
 * Finds the tool call args for a given toolCallId by searching through messages.
 * This is used to reconstruct the input field when converting tool-result parts to StaticToolResult.
 *
 * Searches through messages in reverse order (most recent first) for better performance.
 * Checks both content.parts (v2 format) and toolInvocations (legacy AIV4 format).
 *
 * @param messages - Array of MastraDB messages to search through
 * @param toolCallId - The ID of the tool call to find args for
 * @returns The args object from the matching tool call, or an empty object if not found
 */
export function findToolCallArgs(messages: MastraDBMessage[], toolCallId: string): Record<string, unknown> {
  // Search through all messages in reverse order (most recent first) for better performance
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (!msg || msg.role !== 'assistant') {
      continue;
    }

    // Check both content.parts (v2 format) and toolInvocations (legacy format)
    if (msg.content.parts) {
      // Look for tool-invocation with matching toolCallId (can be in 'call' or 'result' state)
      const toolCallPart = msg.content.parts.find(
        p => p.type === 'tool-invocation' && p.toolInvocation.toolCallId === toolCallId,
      );

      if (toolCallPart && toolCallPart.type === 'tool-invocation') {
        const args = toolCallPart.toolInvocation.args || {};
        if (typeof args === 'object' && Object.keys(args).length > 0) {
          return args;
        }
      }
    }

    // Also check toolInvocations array (AIV4 format)
    if (msg.content.toolInvocations) {
      const toolInvocation = msg.content.toolInvocations.find(inv => inv.toolCallId === toolCallId);

      if (toolInvocation) {
        const args = toolInvocation.args || {};
        if (typeof args === 'object' && Object.keys(args).length > 0) {
          return args;
        }
      }
    }
  }

  // If not found in DB messages, return empty object
  return {};
}
